import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Search, SlidersHorizontal } from "lucide-react";
import { api } from "@/lib/api";
import PerfumeCard from "@/components/PerfumeCard";
import Reveal from "@/components/Reveal";
import { FAMILIES, GENDERS } from "@/lib/constants";

const FAMILY_KEYWORDS = [
  { family: "Gourmand", words: ["sucré", "sucre", "gourmand", "vanille", "caramel", "praline", "doux"] },
  { family: "Frais", words: ["frais", "fraîche", "fraiche", "agrume", "léger", "leger", "marin", "aquatique", "sport"] },
  { family: "Boisé", words: ["boisé", "boise", "bois", "cèdre", "cedre", "santal", "cuir", "vétiver", "vetiver"] },
  { family: "Oriental", words: ["oriental", "ambre", "ambré", "épicé", "epice", "intense", "sensuel", "nuit"] },
  { family: "Floral", words: ["floral", "fleur", "rose", "jasmin", "romantique", "poudré", "poudre"] },
];

const BRAND_KEYWORDS = {
  chanel: "Chanel",
  dior: "Dior",
  ysl: "Yves Saint Laurent",
  "yves saint laurent": "Yves Saint Laurent",
  lancôme: "Lancôme",
  lancome: "Lancôme",
  armani: "Giorgio Armani",
  versace: "Versace",
  gucci: "Gucci",
  boss: "Hugo Boss",
  lacoste: "Lacoste",
  zara: "Zara",
};

export const smartFilter = (perfumes, query) => {
  const q = query.toLowerCase().trim();
  if (!q) return perfumes;

  const constraints = [];
  const priceMatch = q.match(/(?:moins de|max(?:imum)?|sous|jusqu[' ]?[àa]|<=?)\s*(\d+)\s*(?:€|euros?|e\b)?/) || q.match(/(\d+)\s*(?:€|euros?)/);
  if (priceMatch) {
    const max = Number(priceMatch[1]);
    constraints.push((p) => Math.min(...Object.values(p.prices).map(Number)) <= max);
  }
  if (/\b(homme|hommes|masculin|pour lui)\b/.test(q)) constraints.push((p) => p.gender !== "Femme");
  if (/\b(femme|femmes|féminin|feminin|pour elle)\b/.test(q)) constraints.push((p) => p.gender !== "Homme");
  if (/mixte|unisexe/.test(q)) constraints.push((p) => p.gender === "Mixte");
  for (const fk of FAMILY_KEYWORDS) {
    if (fk.words.some((w) => q.includes(w))) {
      constraints.push((p) => p.family === fk.family);
      break;
    }
  }
  for (const [k, b] of Object.entries(BRAND_KEYWORDS)) {
    if (q.includes(k)) {
      constraints.push((p) => p.brand === b);
      break;
    }
  }

  if (constraints.length === 0) {
    return perfumes.filter((p) => `${p.name} ${p.brand} ${p.family} ${p.gender}`.toLowerCase().includes(q));
  }
  // Applique chaque critère seulement s'il reste des résultats (meilleures correspondances sinon)
  let res = perfumes;
  for (const c of constraints) {
    const next = res.filter(c);
    if (next.length > 0) res = next;
  }
  return res;
};

const Boutique = () => {
  const [searchParams] = useSearchParams();
  const [perfumes, setPerfumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [gender, setGender] = useState("");
  const [family, setFamily] = useState("");
  const [brand, setBrand] = useState("");
  const [maxPrice, setMaxPrice] = useState(200);
  const [sort, setSort] = useState("pertinence");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    api
      .get("/perfumes")
      .then((r) => setPerfumes(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const m = searchParams.get("marque");
    if (m) setBrand(m);
  }, [searchParams]);

  const brands = useMemo(() => [...new Set(perfumes.map((p) => p.brand))].sort(), [perfumes]);

  const results = useMemo(() => {
    let res = smartFilter(perfumes, query);
    if (gender) res = res.filter((p) => p.gender === gender);
    if (family) res = res.filter((p) => p.family === family);
    if (brand) res = res.filter((p) => p.brand === brand);
    res = res.filter((p) => Number(p.prices["50"]) <= maxPrice || Number(p.prices["30"]) <= maxPrice);
    if (sort === "prix-asc") res = [...res].sort((a, b) => a.prices["50"] - b.prices["50"]);
    if (sort === "prix-desc") res = [...res].sort((a, b) => b.prices["50"] - a.prices["50"]);
    if (sort === "note") res = [...res].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    return res;
  }, [perfumes, query, gender, family, brand, maxPrice, sort]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Reveal>
        <p className="text-xs font-semibold uppercase tracking-[0.25em] text-[#C59B27]">Catalogue</p>
        <h1 className="mt-2 font-serif text-3xl text-[#1A1714] sm:text-4xl">La Boutique ANM Paris</h1>
      </Reveal>

      <Reveal delay={0.1} className="mt-8">
        <div className="relative">
          <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-[#C59B27]" size={20} />
          <input
            data-testid="smart-search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='Essayez : "parfum sucré pour homme à moins de 40 €"…'
            className="w-full rounded-full border border-[#EADFCB] bg-white py-4 pl-14 pr-6 text-sm text-[#1A1714] shadow-sm outline-none transition-all placeholder:text-[#5C5449]/60 focus:border-[#C59B27] focus:shadow-md"
          />
        </div>
      </Reveal>

      <div className="mt-5 flex flex-wrap items-center gap-2">
        {["", ...GENDERS].map((g) => (
          <button
            key={g || "all"}
            data-testid={`gender-filter-${g || "tous"}`}
            onClick={() => setGender(g)}
            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all active:scale-95 ${
              gender === g ? "border-[#1A1714] bg-[#1A1714] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:border-[#C59B27]/60"
            }`}
          >
            {g || "Tous"}
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-[#EADFCB] sm:block" />
        {["", ...FAMILIES].map((f) => (
          <button
            key={f || "all"}
            data-testid={`family-filter-${f || "toutes"}`}
            onClick={() => setFamily(f)}
            className={`rounded-full border px-4 py-2 text-xs font-semibold transition-all active:scale-95 ${
              family === f ? "border-[#C59B27] bg-[#C59B27] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:border-[#C59B27]/60"
            }`}
          >
            {f || "Toutes familles"}
          </button>
        ))}
        <button
          data-testid="toggle-filters-button"
          onClick={() => setShowFilters(!showFilters)}
          className={`ml-auto flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition-all ${
            showFilters ? "border-[#C59B27] bg-[#F7EEDB] text-[#C59B27]" : "border-[#EADFCB] bg-white text-[#5C5449]"
          }`}
        >
          <SlidersHorizontal size={14} /> Filtres
        </button>
      </div>

      <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="brand-chips-row">
        <button
          data-testid="brand-chip-all"
          onClick={() => setBrand("")}
          className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-all active:scale-95 ${
            brand === "" ? "border-[#FF8A00] bg-[#FF8A00] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:border-[#FF8A00]/60"
          }`}
        >
          Toutes marques
        </button>
        {brands.map((b) => (
          <button
            key={b}
            data-testid={`brand-chip-${b}`}
            onClick={() => setBrand(brand === b ? "" : b)}
            className={`shrink-0 rounded-full border px-4 py-2 text-xs font-semibold transition-all active:scale-95 ${
              brand === b ? "border-[#FF8A00] bg-[#FF8A00] text-white" : "border-[#EADFCB] bg-white text-[#5C5449] hover:border-[#FF8A00]/60"
            }`}
          >
            {b}
          </button>
        ))}
      </div>

      {showFilters && (
        <Reveal className="mt-4 rounded-2xl border border-[#EADFCB] bg-white p-5">
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Marque</label>
              <select
                data-testid="brand-filter-select"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="mt-2 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-3 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              >
                <option value="">Toutes les marques</option>
                {brands.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">
                Budget max : <span className="text-[#1A1714]">{maxPrice} €</span>
              </label>
              <input
                data-testid="price-range-slider"
                type="range"
                min="10"
                max="200"
                step="5"
                value={maxPrice}
                onChange={(e) => setMaxPrice(Number(e.target.value))}
                className="mt-4 w-full accent-[#C59B27]"
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C59B27]">Trier par</label>
              <select
                data-testid="sort-select"
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="mt-2 w-full rounded-xl border border-[#EADFCB] bg-[#FAF8F5] px-3 py-2.5 text-sm outline-none focus:border-[#C59B27]"
              >
                <option value="pertinence">Pertinence</option>
                <option value="prix-asc">Prix croissant</option>
                <option value="prix-desc">Prix décroissant</option>
                <option value="note">Meilleures notes</option>
              </select>
            </div>
          </div>
        </Reveal>
      )}

      <p data-testid="results-count" className="mt-6 text-sm text-[#5C5449]">
        {loading ? "Chargement…" : `${results.length} parfum${results.length > 1 ? "s" : ""} trouvé${results.length > 1 ? "s" : ""}`}
      </p>

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="perfume-catalog-grid">
        {results.map((p, i) => (
          <Reveal key={p.id} delay={Math.min(i, 6) * 0.05}>
            <PerfumeCard p={p} />
          </Reveal>
        ))}
      </div>

      {!loading && results.length === 0 && (
        <div className="py-20 text-center">
          <p className="font-serif text-2xl text-[#1A1714]">Aucun parfum ne correspond à votre recherche</p>
          <p className="mt-2 text-sm text-[#5C5449]">Essayez d'élargir votre budget ou de retirer un filtre.</p>
        </div>
      )}
    </div>
  );
};

export default Boutique;
